/**
 * Display logic and visibility.
 *
 * The important property this file provides is **transitive hiding**. Qualtrics evaluates
 * display logic against raw stored responses, so a question can stay on screen because of an
 * answer to a question that is itself no longer on screen. That produces submissions
 * containing answers to questions the technician was never shown — the exact failure mode
 * this app must not have.
 *
 * Here, visibility is resolved in a single document-order pass and a hidden field reads as
 * *unanswered* to everything downstream. Combine that with `lint.ts` rejecting forward
 * references and the result is a well-founded evaluation with no stale-branch leakage.
 */

import type { Answer, Condition, ContextValue, Field, FormSchema, FormState, Section } from './types.ts';

const CTX = 'ctx.';

const patternCache = new Map<string, RegExp>();

/** Compiles and caches a schema regex. Anchored, because a schema author means the whole value. */
export function compilePattern(pattern: string): RegExp {
  const cached = patternCache.get(pattern);
  if (cached) return cached;
  const re = new RegExp(`^(?:${pattern})$`);
  patternCache.set(pattern, re);
  return re;
}

/** True when a value counts as "the technician gave an answer". */
export function isAnswered(value: Answer | ContextValue | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  // SerialValue
  if (typeof value === 'object' && 'serial' in value) return String(value.serial).trim() !== '';
  return true;
}

/**
 * Flattens an answer to the comparable scalars a condition operates on.
 *
 * A multi-select yields its members; a scalar yields itself; a serial yields its serial
 * string; photos yield nothing comparable (only `answered` is meaningful for them).
 */
function comparableValues(value: Answer | ContextValue | undefined): (string | number | boolean)[] {
  if (value === undefined || value === null) return [];
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [value];
  }
  if (Array.isArray(value)) {
    const out: (string | number | boolean)[] = [];
    for (const item of value) if (typeof item === 'string') out.push(item);
    return out;
  }
  if (typeof value === 'object' && 'serial' in value) return [value.serial];
  return [];
}

/** Loose equality across the string/number boundary. Schemas are hand-written; `4.0` vs `4` is real. */
function scalarEquals(left: string | number | boolean, right: string | number | boolean): boolean {
  if (left === right) return true;
  if (typeof left === 'boolean' || typeof right === 'boolean') return false;
  const ln = Number(left);
  const rn = Number(right);
  if (Number.isFinite(ln) && Number.isFinite(rn)) return ln === rn;
  return String(left) === String(right);
}

function numeric(value: string | number | boolean | undefined): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value === undefined) return Number.NaN;
  return Number(value);
}

/** Reads a field or `ctx.*` reference. Returns `undefined` for hidden or absent. */
export type Lookup = (ref: string) => Answer | ContextValue | undefined;

export function evaluate(condition: Condition, lookup: Lookup): boolean {
  if ('all' in condition) return condition.all.every((c) => evaluate(c, lookup));
  if ('any' in condition) return condition.any.some((c) => evaluate(c, lookup));
  if ('not' in condition) return !evaluate(condition.not, lookup);

  const raw = lookup(condition.field);

  // Both presence operators in one branch. See the note on `Condition` in `types.ts`: they are
  // two singleton members precisely so this narrows and the `never` guard below compiles.
  if (condition.op === 'answered' || condition.op === 'notAnswered') {
    return condition.op === 'answered' ? isAnswered(raw) : !isAnswered(raw);
  }

  const values = comparableValues(raw);

  switch (condition.op) {
    case 'eq':
      return values.some((v) => scalarEquals(v, condition.value));
    case 'neq':
      // Unanswered is "not equal to X", which matches Qualtrics' "Is Not Selected".
      return !values.some((v) => scalarEquals(v, condition.value));
    case 'gt':
      return values.some((v) => numeric(v) > numeric(condition.value));
    case 'gte':
      return values.some((v) => numeric(v) >= numeric(condition.value));
    case 'lt':
      return values.some((v) => numeric(v) < numeric(condition.value));
    case 'lte':
      return values.some((v) => numeric(v) <= numeric(condition.value));
    case 'in':
    case 'includes':
      return values.some((v) => condition.value.some((w) => scalarEquals(v, w)));
    case 'notIn':
    case 'excludes':
      return !values.some((v) => condition.value.some((w) => scalarEquals(v, w)));
    default: {
      // Exhaustiveness guard: an unknown operator must not silently mean "show it".
      const never: never = condition;
      throw new Error(`unknown condition operator: ${JSON.stringify(never)}`);
    }
  }
}

/* ----------------------------------------------------------------- visibility ---- */

export interface Visibility {
  readonly fields: ReadonlySet<string>;
  readonly sections: ReadonlySet<string>;
}

/**
 * Resolves which sections and fields are on screen.
 *
 * One pass, document order. A field is visible when its section is visible and its own
 * `showIf` passes, evaluated against a lookup in which every already-decided hidden field —
 * and every not-yet-decided field — reads as unanswered.
 */
export function computeVisibility(schema: FormSchema, state: FormState): Visibility {
  const visibleFields = new Set<string>();
  const visibleSections = new Set<string>();
  const decided = new Set<string>();

  const lookup: Lookup = (ref) => {
    if (ref.startsWith(CTX)) {
      const key = ref.slice(CTX.length);
      return Object.prototype.hasOwnProperty.call(state.context, key) ? state.context[key] : undefined;
    }
    // Undecided (forward reference) or decided-hidden both read as unanswered.
    if (!decided.has(ref) || !visibleFields.has(ref)) return undefined;
    return state.entries[ref]?.value;
  };

  for (const section of schema.sections) {
    const sectionShown = section.showIf === undefined || evaluate(section.showIf, lookup);
    if (sectionShown) visibleSections.add(section.id);

    for (const field of section.fields) {
      const shown = sectionShown && (field.showIf === undefined || evaluate(field.showIf, lookup));
      if (shown) visibleFields.add(field.id);
      decided.add(field.id);
    }
  }

  return { fields: visibleFields, sections: visibleSections };
}

/** Every field in the schema, in document order, paired with its section. */
export function allFields(schema: FormSchema): { section: Section; field: Field }[] {
  const out: { section: Section; field: Field }[] = [];
  for (const section of schema.sections) {
    for (const field of section.fields) out.push({ section, field });
  }
  return out;
}

/** The sections a technician actually walks, with their visible fields, notes included. */
export function visibleSections(
  schema: FormSchema,
  state: FormState,
  visibility: Visibility = computeVisibility(schema, state),
): { section: Section; fields: Field[] }[] {
  const out: { section: Section; fields: Field[] }[] = [];
  for (const section of schema.sections) {
    if (!visibility.sections.has(section.id)) continue;
    const fields = section.fields.filter((f) => visibility.fields.has(f.id));
    if (fields.length === 0) continue;
    // A section made up entirely of display-only notes is not a step worth paginating.
    if (fields.every((f) => f.kind === 'note')) continue;
    out.push({ section, fields });
  }
  return out;
}
